import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type {
  FinishProductionRequest,
  ManualEntryRequest,
  UpdatePlannedTimesRequest,
} from "@opus-dt/shared-types";
import type { ProductionRunSummary } from "../../types/production.types.js";

export type { ProductionRunSummary } from "../../types/production.types.js";

const activeKey = (lineId: string) => ["production", "active", lineId] as const;
const lineKey = (lineId: string) => ["lines", lineId] as const;

export function useActiveRun(
  lineId: string,
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  return useQuery<ProductionRunSummary | null>({
    queryKey: activeKey(lineId),
    queryFn: () =>
      api.get<ProductionRunSummary | null>(`/api/v1/lines/${lineId}/production-runs/active`),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>, lineId: string) {
  void qc.invalidateQueries({ queryKey: activeKey(lineId) });
  void qc.invalidateQueries({ queryKey: lineKey(lineId) });
  void qc.invalidateQueries({ queryKey: ["lines"] });
}

export function useUpdatePlannedTimes(lineId: string, runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdatePlannedTimesRequest) =>
      api.patch(`/api/v1/lines/${lineId}/production-runs/${runId}/planned-times`, data),
    onSuccess: () => {
      invalidateAll(qc, lineId);
      void qc.invalidateQueries({ queryKey: ["oee", lineId] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    // Consumidor `production/EditPlannedTimesDialog.tsx` já trata localmente.
    meta: { skipErrorToast: true },
  });
}

export function usePauseRun(lineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, observation }: { runId: string; observation?: string }) =>
      api.patch<ProductionRunSummary>(
        `/api/v1/lines/${lineId}/production-runs/${runId}/pause`,
        observation ? { observation } : {}
      ),
    onSuccess: () => invalidateAll(qc, lineId),
    // Consumidores (`production/LineCommandBar.tsx`,
    // `operational-entry/.../PauseObsForm.tsx`) já tratam localmente.
    meta: { skipErrorToast: true },
  });
}

export function useResumeRun(lineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      api.patch<ProductionRunSummary>(`/api/v1/lines/${lineId}/production-runs/${runId}/resume`),
    onSuccess: () => invalidateAll(qc, lineId),
    // Consumidor `production/LineCommandBar.tsx` já trata localmente.
    meta: { skipErrorToast: true },
  });
}

export function useFinishRun(lineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, data }: { runId: string; data: FinishProductionRequest }) =>
      api.patch<ProductionRunSummary>(
        `/api/v1/lines/${lineId}/production-runs/${runId}/finish`,
        data
      ),
    onSuccess: () => invalidateAll(qc, lineId),
    // Consumidor `production/FinishProductionDialog.tsx` já trata localmente.
    meta: { skipErrorToast: true },
  });
}

export function useManualEntry(lineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ManualEntryRequest) =>
      api.post(`/api/v1/lines/${lineId}/manual-entries`, data),
    onSuccess: () => invalidateAll(qc, lineId),
    // Consumidor `pages/DataEntry.tsx` (via QtyForm) já trata localmente.
    meta: { skipErrorToast: true },
  });
}
